import { THEME } from '@common/constants/theme.constant';
import { FieldV2, IFieldV2Props } from '@common/general-ui/field-v2/field-v2.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { useTimeout } from '@common/hooks/use-timeout.hook';
import { SerializedStyles } from '@emotion/react';
import { Input as AntdInput } from 'antd';
import React, { FocusEventHandler, InputHTMLAttributes, ReactNode, useCallback, useEffect, useState } from 'react';
import { css } from 'twin.macro';

export type IInputV2ChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => void;

export interface IInputV2Props<T = string | number, TName = string> extends IFieldV2Props<T, TName> {
    prefix?: ReactNode;
    suffix?: ReactNode;
    minLength?: number;
    maxLength?: number;
    type: InputHTMLAttributes<T>['type'];
    className?: string;
    addonBefore?: string | ReactNode;
    onChange?: IInputV2ChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: FocusEventHandler;
    onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    id?: string;
    autoComplete?: string;
    min?: number;
    max?: number;
    formatValue?: (val: string) => string;
}

export const InputV2 = (props: IInputV2Props): JSX.Element => {
    const { size, errorMessage, helperText, subText, validate: registerOptions, formatValue, ...rest } = props;
    // const inputRef = useRef<AntdInput>(); Have ref!!!
    const [val, setVal] = useState(props.defaultValue || props.value);
    const [isVisible, setIsVisible] = useState(props.autoComplete !== 'off');
    const { start } = useTimeout(() => {
        setIsVisible(true);
    }, 0);

    useEffect(() => {
        start(undefined);
    }, [start]);

    useEffect(() => {
        if (props.value !== undefined) {
            setVal(formatValue ? formatValue(props.value as string) : props.value);
        }
    }, [formatValue, props.value]);

    useEffect(() => {
        if (props.defaultValue !== undefined) {
            setVal(formatValue ? formatValue(props.defaultValue as string) : props.defaultValue);
        }
    }, [formatValue, props.defaultValue]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            setVal(formatValue ? formatValue(newValue) : newValue);
            props.onChange &&
                props.onChange({
                    ...e,
                    target: {
                        ...e.target,
                        value: formatValue ? (formatValue(newValue) as string) : newValue,
                    },
                });
        },
        [formatValue, props],
    );

    return (
        <FieldV2 {...props} tw="relative">
            <div tw="relative">
                {props.readOnly && <Readonly />}
                {/* https://stackoverflow.com/questions/15738259/disabling-chrome-autofill */}
                {isVisible && (
                    <AntdInput
                        {...rest}
                        value={val}
                        onChange={handleChange}
                        maxLength={props.maxLength}
                        css={inputStyles.input(!!props.readOnly, !!errorMessage)}
                        tabIndex={props.readOnly ? -1 : 0}
                    />
                )}
            </div>
        </FieldV2>
    );
};

export const inputStyles = {
    input: (readOnly: boolean, error: boolean): (SerializedStyles | boolean)[] => [
        css`
            width: 100%;
            height: 48px;
            line-height: 24px;
            border: 1px solid ${THEME.GRAY_300};
            border-radius: 4px;
            font-weight: 300;
            font-size: ${THEME.BASE_FONT_SIZE};
            color: ${THEME.BLACK};
            padding: 12px;
            box-sizing: border-box;
            text-overflow: ellipsis !important;

            && .ant-input-prefix {
                color: ${THEME.GRAY};
                font-weight: 600;
                margin: 2px 0 0;
            }

            ::placeholder {
                color: ${THEME.TEXT_LIGHT_GRAY};
            }

            :focus {
                outline-color: transparent;
                outline-style: none;
                box-shadow: none;
                border-color: ${THEME.TEXT_BLACK};
            }

            :disabled,
            :disabled:hover {
                border-color: ${THEME.GRAY_300};
                background-color: ${THEME.GRAY_100};
                color: ${THEME.GRAY};
            }
        `,
        readOnly &&
            css`
                border: 1px solid ${THEME.GRAY_300};
                :hover {
                    border: 1px solid ${THEME.GRAY_300};
                }
                :focus {
                    border: 1px solid ${THEME.GRAY_300};
                    outline-color: transparent;
                    outline-style: none;
                    box-shadow: none;
                }
                .ant-input-affix-wrapper-readonly {
                    border: 1px solid ${THEME.GRAY_300};
                }
                &&.ant-input-affix-wrapper:focus-within {
                    outline-color: transparent;
                    outline-style: none;
                    box-shadow: none;
                }
            `,
        error &&
            css`
                border-color: ${THEME.PINK};
            `,
    ],
};
